"use client";

// Flat-key i18n context for module bundles.
//
// Module i18n files (`modules/*/i18n/*.json`) ship a flat dict whose keys
// can collide with parent paths in next-intl's nested message tree (e.g.
// `"fleet.machine"` AND `"fleet.machine.interni_broj"` both exist). Rather
// than force every module author to introduce a sentinel suffix, we keep
// the dict flat and resolve through this dedicated context — `useFlatT()`
// looks up the exact dotted key, returning the raw key when the bundle
// lacks an entry.
//
// The substrate's page-factory and EntityDataTable client call
// `useT()` (the unified resolver) which tries the flat bundle first for
// module-namespaced keys, then falls through to `useTranslations()` for
// platform keys (`platform.save`, `platform.status`, etc).

import * as React from "react";
import { useTranslations } from "next-intl";
import type { SupportedLocale } from "./provider";
import { latnToCyrl } from "./transliterator";

export type FlatBundles = Partial<Record<SupportedLocale, Record<string, string>>>;

interface FlatI18nContextValue {
  locale: SupportedLocale;
  bundles: FlatBundles;
}

const FlatI18nContext = React.createContext<FlatI18nContextValue | null>(null);

export interface FlatI18nProviderProps {
  children: React.ReactNode;
  /** Per-locale flat bundles. `sr-Cyrl` is derived from `sr-Latn` if absent. */
  bundles: FlatBundles;
  locale: SupportedLocale;
}

export function FlatI18nProvider({
  children,
  bundles,
  locale,
}: FlatI18nProviderProps): React.ReactElement {
  const value = React.useMemo<FlatI18nContextValue>(
    () => ({ locale, bundles }),
    [locale, bundles],
  );
  return (
    <FlatI18nContext.Provider value={value}>{children}</FlatI18nContext.Provider>
  );
}

/**
 * Resolve a flat dotted key against the active locale's module bundle.
 * Returns the raw `key` when the bundle is missing, the locale isn't loaded,
 * or the key isn't present.
 *
 * For `sr-Cyrl` we transliterate the `sr-Latn` value at runtime when the
 * bundle wasn't shipped explicitly — same policy as the I18nProvider's
 * platform bundle.
 */
export function useFlatT(): (key: string) => string {
  const ctx = React.useContext(FlatI18nContext);
  return React.useCallback(
    (key: string): string => {
      if (!ctx) return key;
      const direct = ctx.bundles[ctx.locale]?.[key];
      if (direct !== undefined) return direct;
      // sr-Cyrl falls back to sr-Latn + transliteration so authors don't
      // have to ship two bundles per module.
      if (ctx.locale === "sr-Cyrl") {
        const latn = ctx.bundles["sr-Latn"]?.[key];
        if (latn !== undefined) return latnToCyrl(latn);
      }
      return key;
    },
    [ctx],
  );
}

/**
 * Unified i18n resolver for substrate / module callers.
 *
 * Resolution order:
 *   1. Flat module bundle — covers `<moduleId>.<entity>.<field>` keys,
 *      including parent / child collisions next-intl can't handle.
 *   2. next-intl nested tree — covers platform keys (`platform.save`).
 *   3. Raw key — when neither bundle resolves the path.
 *
 * Designed as a drop-in for `useTranslations()`: callers do `const t = useT()`
 * then `t("fleet.machine.naziv_masine")` and get the right answer regardless
 * of which bundle holds the entry.
 */
export function useT(): (key: string) => string {
  const flat = useFlatT();
  // `useTranslations()` is the next-intl client hook; we pass no namespace
  // so `t(fullKey)` walks from the message-tree root.
  const t = useTranslations();
  return React.useCallback(
    (key: string): string => {
      const fromFlat = flat(key);
      if (fromFlat !== key) return fromFlat;
      // `t.has()` distinguishes "missing key" from "key resolves to itself"
      // so we can return the raw key cleanly when neither bundle has it.
      if (t.has(key)) return t(key);
      return key;
    },
    [flat, t],
  );
}
